/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import {
    ActorRef,
    ActorRefFrom, assertEvent, assign, fromPromise, sendTo, setup,
} from 'xstate';
import { produce } from 'immer';
import { getBFFApiClient } from '@acp/feature/auth';
import HttpStatusCodes from 'http-status-codes';
import {
    APIResponse, PropertyDocument, PropertyDocumentUtil, TrackingAction, TrackingActionCategory,
} from '@acp/shared';
import { addTrackingEvent } from '../../../../hooks/use-tracking-event';

type DocumentUploadProperty = {
    type: 'COMPLIANCE_PROPERTY' | 'NON-MANAGED';
    id: number;
    address: string;
};

type DocumentMachineEvents = { type: 'START_UPLOAD'; property: DocumentUploadProperty; file: File }
| { type: 'UPLOAD_COMPLETE'; id: string; property: DocumentUploadProperty }
| { type: 'UPLOAD_FAILED'; id: string; property: DocumentUploadProperty }
| { type: 'CLEAR_COMPLETED' }
| { type: 'REMOVE_FILE_UPLOAD'; id: string };

type FileUploadMachineEvents = { type: 'CANCEL' }
| { type: 'SET_UPLOAD_PERCENTAGE'; percentage: number }
| { type: 'RETRY' }
| { type: 'START' };

const uploadFileActor = fromPromise(
    async (
        {
            input,
        }: {
            input: {
                parent: ActorRef<any, FileUploadMachineEvents>;
                property: DocumentUploadProperty;
                file: File;
                abortController: AbortController;
            };
        }
    ) => {
        const form = new FormData();
        form.append('file', input.file);

        const uploadFileApiUrl = input.property.type === 'NON-MANAGED'
            ? `/v1/integrated-property/${input.property.id}/documents`
            : `/v1/property/${input.property.id}/documents`;

        const response = await getBFFApiClient()
            .post<APIResponse<PropertyDocument>>(
            uploadFileApiUrl,
            form,
            {
                onUploadProgress: (progressEvent) => {
                    const percentage = progressEvent.progress;
                    if (percentage === undefined) return;

                    input.parent.send({
                        type: 'SET_UPLOAD_PERCENTAGE',
                        percentage: Math.round(percentage * 100),
                    });
                },
                signal: input.abortController.signal,
            }
        );

        if (response.status !== HttpStatusCodes.OK || !response.data.success) {
            throw new Error(response.data.error ?? 'Unknown error');
        }

        // add a tracking event for the upload
        addTrackingEvent({
            category: TrackingActionCategory.DocumentUpload,
            action: TrackingAction.DropzoneUploadResult,
            context: {
                ...input.property,
                fileType: input.file.type,
                fileSize: input.file.size,
                fileName: input.file.name,
            },
        });

        return response.data.data;
    }
);

export const documentUploadMachine = setup({
    types: {
        context: {} as {
            property: DocumentUploadProperty;
            abortController: AbortController;
            file: File;
            uploadPercentage: number;
            parentRef: ActorRef<any, DocumentMachineEvents>;
            error: string | null;
            maxFileSizeBytes: number;
        },
        input: {} as {
            property: DocumentUploadProperty;
            file: File;
            parentRef: ActorRef<any, DocumentMachineEvents>;
            maxFileSizeBytes: number;
        },
        events: {} as FileUploadMachineEvents,
    },
    actors: {
        uploadFile: uploadFileActor,
    },
    actions: {
        abortUpload: ({ context }) => {
            context.abortController.abort();
        },
        removeFileUploadMachine: sendTo(({ context }) => context.parentRef, ({ self }) => ({
            type: 'REMOVE_FILE_UPLOAD',
            id: self.id,
        })),
        notifySuccess: sendTo(({ context }) => context.parentRef, ({ self, context }) => ({
            type: 'UPLOAD_COMPLETE',
            id: self.id,
            property: context.property,
        })),
        notifyFailed: sendTo(({ context }) => context.parentRef, ({ self, context }) => ({
            type: 'UPLOAD_FAILED',
            id: self.id,
            property: context.property,
        })),
    },
    guards: {
        isInvalidFileType: ({ context }) => !PropertyDocumentUtil.isValid(
            context.file.name,
            context.file.type
        ),
        isInvalidFileSize: ({ context }) => context.file.size > context.maxFileSizeBytes,
    },
}).createMachine({
    initial: 'queued',
    context: ({ input }) => ({
        abortController: new AbortController(),
        file: input.file,
        maxFileSizeBytes: input.maxFileSizeBytes,
        uploadPercentage: 0,
        parentRef: input.parentRef,
        property: input.property,
        error: null,
    }),
    states: {
        queued: {
            on: {
                START: {
                    target: 'uploading',
                },
                CANCEL: {
                    target: 'completed',
                    actions: 'abortUpload',
                },
            },
            always: [
                {
                    guard: 'isInvalidFileSize',
                    target: 'failed',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.error = 'File size is too large';
                    })),
                },
                {
                    guard: 'isInvalidFileType',
                    target: 'failed',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.error = 'Invalid file type';
                    })),
                },
            ],
        },
        uploading: {
            invoke: {
                src: 'uploadFile',
                input: ({ self, context }) => ({
                    property: context.property,
                    file: context.file,
                    parent: self,
                    abortController: context.abortController,
                }),
                onDone: {
                    target: 'success',
                },
                onError: {
                    target: 'failed',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.error = (event.error as Error).message;
                    })),
                },
            },
            on: {
                CANCEL: {
                    target: 'completed',
                    actions: 'abortUpload',
                },
                SET_UPLOAD_PERCENTAGE: {
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        assertEvent(event, 'SET_UPLOAD_PERCENTAGE');
                        draft.uploadPercentage = event.percentage;
                    })),
                },
            },
        },
        success: {
            entry: 'notifySuccess',
            on: {
                CANCEL: 'completed',
            },
        },
        failed: {
            entry: 'notifyFailed',
            on: {
                CANCEL: 'completed',
            },
        },
        completed: {
            type: 'final',
            entry: 'removeFileUploadMachine',
        },
    },
});

const documentUploadQueueMachine = setup({
    types: {
        events: {} as DocumentMachineEvents,
        context: {} as {
            queue: ActorRefFrom<typeof documentUploadMachine>[];
            uploadCapacity: number;
            maxFileSizeBytes: number;
        },
        input: {} as {
            uploadCapacity: number;
            maxFileSizeBytes: number;
        },
    },
    actors: {
        fileUpload: documentUploadMachine,
    },
    actions: {
        processQueue: ({ context }) => {
            // Simple queue mechanism that will ensure
            // that only a certain number of uploads (contex.uploadCapacity)
            // are running at the same time.
            const hasQueuedUploaders = context.queue.some(
                (uploader) => uploader.getSnapshot().matches('queued')
            );

            if (!hasQueuedUploaders) return;

            const runningUploaders = context.queue.filter(
                (uploader) => uploader.getSnapshot().matches('uploading')
            );

            if (runningUploaders.length >= context.uploadCapacity) return;

            const availableCapacity = context.uploadCapacity - runningUploaders.length;

            if (availableCapacity <= 0) return;

            let usedCapacity = 0;

            for (const uploader of context.queue) {
                if (uploader.getSnapshot().value === 'queued') {
                    uploader.send({ type: 'START' });

                    usedCapacity += 1;
                }

                if (usedCapacity >= availableCapacity) break;
            }
        },
        pushFileToQueue: assign(({
            context, event, spawn, self,
        }) => produce(context, (draft) => {
            assertEvent(event, 'START_UPLOAD');
            draft.queue.push(
                spawn('fileUpload', {
                    id: `file-${window.crypto.randomUUID()}`,
                    input: {
                        file: event.file,
                        property: event.property,
                        parentRef: self,
                        maxFileSizeBytes: context.maxFileSizeBytes,
                    },
                })
            );
        })),
        uploadedCompleted: (_, _params: {
            property: DocumentUploadProperty;
        }) => {},
        uploadFailed: (_, _params: {
            property: DocumentUploadProperty;
        }) => {},
        uploadStarted: (_, _params: {
            property: DocumentUploadProperty;
        }) => {},
        removeFileUploadMachine: assign(
            ({ context }, { id }: { id: string }) => produce(context, (draft) => {
                draft.queue = context.queue.filter(
                    (upload) => upload.id !== id
                );
            })
        ),
        clearCompleted: assign(
            ({ context }) => produce(context, (draft) => {
                draft.queue = context.queue.filter(
                    (upload) => !upload.getSnapshot().matches('success')
                        && !upload.getSnapshot().matches('failed')
                );
            })
        ),
        queueLengthChanged: (_, _params: {
            activeCount: number;
        }) => {},
    },
}).createMachine({
    id: 'documentUploadQueue',
    initial: 'idle',
    context: ({ input }) => ({
        queue: [],
        uploadCapacity: input.uploadCapacity,
        maxFileSizeBytes: input.maxFileSizeBytes,
    }),
    states: {
        idle: {
            after: {
                1000: 'enqueue',
            },
        },
        enqueue: {
            entry: 'processQueue',
            after: {
                1000: 'idle',
            },
        },
    },
    on: {
        START_UPLOAD: {
            actions: [
                {
                    type: 'pushFileToQueue',
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (uploader) => uploader.getSnapshot().matches('uploading')
                                    || uploader.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        UPLOAD_COMPLETE: {
            actions: [
                {
                    type: 'uploadedCompleted',
                    params: ({ event }) => ({
                        property: event.property,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (uploader) => uploader.getSnapshot().matches('uploading')
                                    || uploader.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        UPLOAD_FAILED: {
            actions: [
                {
                    type: 'uploadFailed',
                    params: ({ event }) => ({
                        property: event.property,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (upload) => upload.getSnapshot().matches('uploading')
                                    || upload.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        CLEAR_COMPLETED: {
            actions: 'clearCompleted',
        },
        REMOVE_FILE_UPLOAD: {
            actions: [
                {
                    type: 'removeFileUploadMachine',
                    params: ({ event }) => ({
                        id: event.id,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (upload) => upload.getSnapshot().matches('uploading')
                                    || upload.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
    },
});

export { documentUploadQueueMachine };
